import React from "react";
import { Controller } from "react-hook-form";
import { Eye, EyeOff, Check } from "lucide-react";
import FileUploadSection from "./FileUploadSection";
import SelectionDropdown from "./SelectionDropdown";

const UserFormFields = ({
  register,
  control,
  errors,
  formValues,
  watch,
  setValue,
  isEditMode,
  showPassword,
  setShowPassword,
  showConfirm,
  setShowConfirm,
  // File handlers
  handleUserFilesChange,
  handleRemoveUserFile,
  getFileName,
  userFileError,
  // Selection dropdown props
  selectedCustomersData,
  availableCustomers,
  loadingCustomers,
  showCustomersDropdown,
  setShowCustomersDropdown,
  customerSearchTerm,
  setCustomerSearchTerm
}) => {
  return (
    <>
      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="fullName" className="form-label">Full Name</label>
          <input
            id="fullName"
            className={`form-input ${errors.fullName ? "input-error" : ""}`}
            placeholder="Sofie Gonzales"
            {...register("fullName", { required: "Full Name is required" })}
            aria-invalid={!!errors.fullName}
          />
          {errors.fullName && <p className="error-text">{errors.fullName.message}</p>}
        </div>

        <div className="form-field">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            id="email"
            type="email"
            className={`form-input ${errors.email ? "input-error" : ""}`}
            placeholder="sofiegonzales@gmail.com"
            {...register("email", { 
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email format"
              }
            })}
            aria-invalid={!!errors.email}
          />
          {errors.email && <p className="error-text">{errors.email.message}</p>}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="companyName" className="form-label">Company Name</label>
          <input
            id="companyName"
            className={`form-input ${errors.companyName ? "input-error" : ""}`}
            placeholder="Name"
            {...register("companyName")}
            aria-invalid={!!errors.companyName}
          />
          {errors.companyName && <p className="error-text">{errors.companyName.message}</p>}
        </div>

        <div className="form-field">
          <label htmlFor="phone" className="form-label">Phone</label>
          <input
            id="phone"
            type="tel"
            className={`form-input ${errors.phone ? "input-error" : ""}`}
            placeholder="+1 (555) 000-0000"
            {...register("phone")}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && <p className="error-text">{errors.phone.message}</p>}
        </div>

        <div className="form-field">
          <label htmlFor="role" className="form-label">Role</label>
          <select
            id="role"
            className={`form-select ${errors.role ? "input-error" : ""}`}
            {...register("role", { required: "Role is required" })}
            aria-invalid={!!errors.role}
          >
            <option value="">Select Role</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="accountingManager">Accounting Manager</option>
            <option value="accountingIn">Accounting In</option>
            <option value="accountingOut">Accounting Out</option>
            <option value="freightBroker">Freight Broker</option>
            <option value="dispatcher">Dispatcher</option>
            <option value="Pre-dispatcher">Pre-dispatcher</option>
            <option value="partner">Partner</option>
            <option value="salesAgent">Sales Agent</option>
            <option value="bidAgent">Bid Agent</option>
          </select>
          {errors.role && <p className="error-text">{errors.role.message}</p>}
        </div>
      </div>

      {!isEditMode && (
        <div className="form-grid">
          <div className="form-field password-field roboto-regular">
            <label htmlFor="password" className="form-label">Password</label>
            <div className={`password-input-container ${errors.password ? "input-error" : ""}`}>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="form-input"
                placeholder="Password"
                {...register("password", { 
                  required: !isEditMode ? "Password is required" : false,
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters"
                  }
                })}
                autoComplete="new-password"
                aria-invalid={!!errors.password}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="eye-icon" /> : <Eye className="eye-icon" />}
              </button>
            </div>
            {errors.password && <p className="error-text">{errors.password.message}</p>}
          </div>

          <div className="form-field confirm-password roboto-regular">
            <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
            <div className={`password-input-container ${errors.confirmPassword ? "input-error" : ""}`}>
              <input
                id="confirmPassword"
                type={showConfirm ? "text" : "password"}
                className="form-input"
                placeholder="Confirm new password"
                {...register("confirmPassword", {
                  validate: (value) => 
                    value === watch("password") || "Passwords do not match"
                })}
                autoComplete="new-password"
                aria-invalid={!!errors.confirmPassword}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="password-toggle"
                aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
              >
                {showConfirm ? <EyeOff className="eye-icon" /> : <Eye className="eye-icon" />}
              </button>
            </div>
            {errors.confirmPassword && <p className="error-text">{errors.confirmPassword.message}</p>}
          </div>
        </div>
      )}

      <div className="form-grid form-grid--single">
        <div className="form-field switch-field">
          <span className="form-label">Active</span>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <button
                id="active-toggle"
                type="button"
                className={`toggle-switch ${field.value ? "is-on" : ""}`}
                onClick={() => field.onChange(!field.value)}
                role="switch"
                aria-checked={field.value}
              >
                <span className="toggle-track">
                  <span className="toggle-thumb">
                    {field.value && <Check className="toggle-icon" size={14} />}
                  </span>
                </span>
              </button>
            )}
          />
        </div>
      </div>

      <FileUploadSection
        title="User Documents"
        formValues={formValues}
        fileFieldName="userFiles"
        existingFilesFieldName="existingUserFiles"
        fileError={userFileError}
        onFileChange={handleUserFilesChange}
        onRemoveFile={handleRemoveUserFile}
        getFileName={getFileName}
      />

      {isEditMode && (
        <SelectionDropdown
          title="Allowed Customers"
          label="Select customers this user can access"
          selectedItems={selectedCustomersData}
          availableItems={availableCustomers}
          loading={loadingCustomers}
          searchTerm={customerSearchTerm}
          onSearchChange={setCustomerSearchTerm}
          formFieldName="allowedCustomers"
          formValues={formValues}
          setValue={setValue}
          getItemDisplayName={(customer) => customer.companyName || customer.name || 'Unnamed Customer'}
          getItemId={(customer) => (customer.id || customer._id).toString()}
          itemType="customers"
          showDropdown={showCustomersDropdown}
          setShowDropdown={setShowCustomersDropdown}
        />
      )}
    </>
  );
};

export default UserFormFields;

